import classNames from 'classnames';
import React, {useCallback, useRef, useState} from 'react';

import {Permissions} from 'auth/PermissionConstants';
import * as ExportConstants from 'constants/ExportConstants';
import {Review, Snapshot} from 'notebook/reviews/ReviewConstants';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {RelativeTime} from 'ui-library/Times';
import APIUtil from 'util/APIUtil';
import {capitalize, typeToName} from 'util/NameUtil';
import * as TaskRunner from 'util/TaskRunner';
import {OngoingTaskResponse} from 'util/TaskRunner';

import postReviewActionServerUpdate from './postReviewActionServerUpdate';
import {isReviewUpdatedResponse, retryReviewSnapshot, ReviewUpdatedResponse} from './ReviewDAO';

function SnapshotText({
  snapshot,
  apiType,
  showCreatedTime,
}: {
  snapshot: Snapshot;
  apiType: string;
  showCreatedTime: boolean;
}): JSX.Element {
  const {STATUS} = ExportConstants;
  const fileType = capitalize(typeToName(apiType));
  switch (snapshot.status) {
    case STATUS.PENDING:
    case STATUS.RUNNING:
      if (showCreatedTime) {
        return (
          <span>
            {fileType} snapshot started <RelativeTime datetime={snapshot.createdAt} />.
          </span>
        );
      } else {
        return <span>{fileType} snapshot started.</span>;
      }
    case STATUS.FAILED:
      return <span>{fileType} snapshot failed.</span>;
    case STATUS.SUCCEEDED:
      if (showCreatedTime) {
        return (
          <span>
            {fileType} snapshot succeeded <RelativeTime datetime={snapshot.createdAt} />.
          </span>
        );
      } else {
        return <span>{fileType} snapshot succeeded.</span>;
      }
    default:
      throw new Error(`Unexpected snapshot status ${snapshot.status}`);
  }
}

const STATUS_TO_ICON: Record<string, BenchlingIconName> = {
  [ExportConstants.STATUS.PENDING]: 'request-in-progress',
  [ExportConstants.STATUS.RUNNING]: 'request-in-progress',
  [ExportConstants.STATUS.FAILED]: 'request-failed',
  [ExportConstants.STATUS.SUCCEEDED]: 'download',
};

function SnapshotIcon({snapshot, className}: {snapshot: Snapshot; className?: string}): JSX.Element {
  const icon = STATUS_TO_ICON[snapshot.status];
  return <BenchlingIcon icon={icon} className={className} />;
}

export default function SnapshotInfo({
  snapshot,
  apiType,
  showCreatedTime,
}: {
  snapshot: Snapshot;
  apiType: string;
  showCreatedTime: boolean;
}): JSX.Element {
  const snapshotMessage = (
    <div className="u-small-spacing-top">
      <SnapshotIcon snapshot={snapshot} className="icon u-small-spacing-right text-center" />
      <SnapshotText apiType={apiType} snapshot={snapshot} showCreatedTime={showCreatedTime} />
    </div>
  );
  if (snapshot.url) {
    return <a href={snapshot.url}>{snapshotMessage}</a>;
  }
  return snapshotMessage;
}

/**
 * Surface a retry action for failed snapshot, with similar interface to HistoryItemIcon + HistoryItemSummary.
 *
 * `refetch` will be invoked after the snapshot retry completes. Pass `onReviewChange` (e.g. from
 * `ReviewBadgeContext`) for non-Entry reviewables, since the default store sync is Entry-only. Either
 * path polls the snapshot task to completion before calling `refetch`.
 */
export function LastSnapshotFailedIconAndSummary({
  fileId,
  reviewId,
  refetch,
  onReviewChange,
}: {
  fileId: string;
  reviewId: string;
  refetch?: () => void;
  onReviewChange?: (
    fileId: string,
    review: Review,
    filePermissions: Permissions,
    snapshotTask: OngoingTaskResponse | null
  ) => void;
}): JSX.Element {
  // Capture when the user is attempting the retry + we are polling to give feedback.
  const [isRetrying, setIsRetrying] = useState(false);

  // In case the component re-renders with new callbacks, make sure we use the latest ones.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  const onReviewChangeRef = useRef(onReviewChange);
  onReviewChangeRef.current = onReviewChange;

  const handleClick = useCallback(async () => {
    setIsRetrying(true);
    const {review, filePermissions, snapshotTask} = await retryReviewSnapshot(fileId, reviewId);
    if (onReviewChangeRef.current) {
      onReviewChangeRef.current(fileId, review, filePermissions, snapshotTask);
      if (snapshotTask) {
        TaskRunner.resumeTask<ReviewUpdatedResponse>(snapshotTask.status_url)
          .then((response) => {
            if (isReviewUpdatedResponse(response)) {
              onReviewChangeRef.current?.(fileId, response.review, response.filePermissions, null);
            }
          })
          .always(() => {
            refetchRef.current?.();
            setIsRetrying(false);
          });
      } else {
        refetchRef.current?.();
        setIsRetrying(false);
      }
    } else {
      postReviewActionServerUpdate(fileId, review, filePermissions, snapshotTask, {
        refetch: () => {
          refetchRef.current?.();
          setIsRetrying(false);
        },
      });
    }
  }, [fileId, reviewId]);

  return (
    <>
      <BenchlingIcon
        icon={isRetrying ? 'refresh' : 'request-in-progress'}
        className={classNames('icon u-small-spacing-right text-center', {'glyphicon-spin': isRetrying})}
      />
      <span>
        Latest {typeToName(APIUtil.apiIdToType(fileId))} snapshot failed.{' '}
        {!isRetrying ? <a onClick={handleClick}>Retry?</a> : 'Retrying...'}
      </span>
    </>
  );
}
